import {
  type AfterResponseHook,
  type BeforeRequestHook,
  configureApiBaseUrls,
  configureApiClient,
  configureApiHooks,
} from '@dashhudson/api-client/runtime';

import { attemptTokenRefresh, isTokenErrorDescription } from '@/apis/auth-refresh';
import { env } from '@/env';
import { IMPERSONATED_USER_ID_KEY } from '@/stores/impersonator';
import { useTokenStore } from '@/stores/token';
import { browserStorageGetSessionItem } from '@/utils/browserStorage';

/** Adds auth + impersonation headers; normalizes `ctx.init.headers` via `new Headers()`. Exported for tests. */
export const beforeRequest: BeforeRequestHook = (ctx) => {
  if (ctx.url.includes('[object Object]')) {
    throw new Error(
      `Refusing to send request with object-interpolated URL: ${ctx.url}. ` +
        `Pass a primitive identifier (e.g. an id) into the URL template.`,
    );
  }
  const headers = new Headers(ctx.init.headers);
  const token = useTokenStore().token;
  // Don't overwrite a caller-supplied Authorization header
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  const impersonatedUserId = browserStorageGetSessionItem(IMPERSONATED_USER_ID_KEY);
  if (impersonatedUserId) {
    headers.set('X-On-Behalf-Of', impersonatedUserId);
  }
  ctx.init.headers = headers;
};

/** On a 401 token error: refresh once and retry, logout on failure. Mirrors the axios interceptor. Exported for tests. */
export const afterResponse: AfterResponseHook = async (ctx) => {
  if (ctx.response.status !== 401) return undefined;
  const tokenStore = useTokenStore();
  if (!tokenStore.token || tokenStore.isShareToken) return undefined;
  const body = await ctx.response
    .clone()
    .json()
    .catch(() => undefined);
  if (!isTokenErrorDescription(body?.description)) return undefined;
  const success = await attemptTokenRefresh();
  if (success) {
    return ctx.retry();
  }
  return undefined;
};

/** Bootstraps the api-client fetch clients. Call only at app boot + test setup — runtime state is global. */
export function initApiClient() {
  if (!env.libraryApiUrl) {
    throw new Error('initApiClient: VITE_LIBRARY_API_URL is not set.');
  }
  if (!env.libraryIntelligenceApiUrl) {
    throw new Error('initApiClient: VITE_LIBRARY_INTELLIGENCE_API_URL is not set.');
  }
  // Wrapper so MSW's patched global fetch is picked up in tests
  configureApiClient((input, init) => globalThis.fetch(input, init));
  configureApiBaseUrls({
    library: env.libraryApiUrl,
    'library-intelligence': env.libraryIntelligenceApiUrl,
    instagram: env.instagramApiUrl,
    linkedin: env.linkedinApiUrl,
    shorturl: env.shortURLServiceApiUrl,
  });
  configureApiHooks({ beforeRequest, afterResponse });
}
